import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import momentRange from 'moment-range';
import moment from 'moment-timezone';

import { ChartSortModes, FieldKinds, Template } from '@site-mate/dashpivot-shared-library';

import { TemplatesService } from 'app/apps/templates.service';
import { ChartBreakdownOption } from 'app/shared/model/charts/chart-breakdown-option.model';
import { ChartBreakdownStaticOptions } from 'app/shared/model/charts/chart-breakdown-static-options.model';
import { ChartDataIntervalOptions } from 'app/shared/model/charts/chart-data-interval-options.model';
import { ChartDataIntervals } from 'app/shared/model/charts/chart-data-intervals.enum';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartStaticBreakdownOptions } from 'app/shared/model/charts/chart-static-breakdown-options';
import { RatioChartViewVariants } from 'app/shared/model/charts/ratio-chart-view-variants.model';
import { TimeFrame } from 'app/shared/model/charts/time-frame.model';
import { ChartUtilsService } from 'app/shared/service/chart-utils.service';
import { DateRangeService } from 'app/shared/service/date-range.service';
import { RatioChartDataParserService } from 'app/shared/service/ratio-chart-data-parser.service';

// The require()-based block is for browsers, the import-based is for tests execution
const { extendMoment } = typeof require === 'function' ? require('moment-range') : momentRange;

const { range } = extendMoment(moment as any);

@Component({
  selector: 'cc-ratio-chart-settings',
  templateUrl: './ratio-chart-settings.component.html',
  styleUrls: ['./ratio-chart-settings.component.scss'],
})
export class RatioChartSettingsComponent implements OnInit {
  private readonly customTimeframe: TimeFrame;

  readonly showAsOptions = RatioChartViewVariants;
  readonly noneBreakdownOption = ChartBreakdownStaticOptions.none;
  readonly sortModes = [ChartSortModes.Alphabetical, ChartSortModes.Volume];

  @Input() chart: ChartSettings;
  @Input() teamId: string;
  @Input() templates: Template[] = [];
  @Input() dataDateRange: momentRange.DateRange;
  @Output() onChange = new EventEmitter<ChartSettings>();

  breakdownOptions: ChartBreakdownOption[] = [];
  template?: Template;
  dateUnits: (typeof ChartDataIntervalOptions.day)[] = [];
  timeFrames: TimeFrame[] = [];
  timeFrame: TimeFrame;
  isChartSetup = false;

  constructor(
    private readonly chartUtilsService: ChartUtilsService,
    private readonly ratioCharDataParserService: RatioChartDataParserService,
    private readonly templatesService: TemplatesService,
    private readonly dateRangeService: DateRangeService,
  ) {
    this.customTimeframe = this.dateRangeService.getCustomRange();
  }

  get isSortable() {
    return this.ratioCharDataParserService.sortableShowModes.includes(this.chart.showAs);
  }

  ngOnInit() {
    this.timeFrames = this.dateRangeService.getRanges();
    this.timeFrame =
      this.timeFrames.find((timeframe) => this.chart.timeframe === timeframe.value) || this.customTimeframe;
    this.dateUnits = this.getDateUnits(this.timeFrame.range);
    this.chart.breakdownBy =
      this.chart.breakdownBy !== ChartStaticBreakdownOptions.None ? this.chart.breakdownBy : null;
    this.onTemplateChange();
    this.onSortChange();
  }

  onTemplateChange() {
    this.template = this.templates.find((template) => template._id === this.chart.templateId);
    this.isChartSetup = this.chartUtilsService.isChartSetup(this.chart);

    if (!this.template) {
      return;
    }

    this.chart.templateId = this.template._id;
    this.setBreakdownOptions();
  }

  onTimeFrameChange() {
    let dateFrame: momentRange.DateRange;
    const timeFrame = this.timeFrames.find((tf) => tf.value === this.chart.timeframe);

    if (timeFrame) {
      this.timeFrame = timeFrame;
      dateFrame = timeFrame.range;
    } else {
      this.timeFrame = this.customTimeframe;
      this.chart.timeframe = this.customTimeframe.value;
      dateFrame = range();
    }

    this.dateUnits = this.getDateUnits(dateFrame);
    const dateUnit = this.dateUnits.find((unit) => unit.value === this.chart.dateUnit) || this.dateUnits[0];
    this.chart.dateUnit = dateUnit.value;
    this.isChartSetup = this.chartUtilsService.isChartSetup(this.chart);
    this.onChange.emit(this.chart);
  }

  onShowAsChange() {
    this.onChange.emit(this.chart);
  }

  onBreakdownChange() {
    this.onChange.emit(this.chart);
    this.isChartSetup = this.chartUtilsService.isChartSetup(this.chart);
  }

  onSortChange() {
    this.chart.sortMode = this.chart.sortMode || ChartSortModes.Alphabetical;
    if (this.isChartSetup) {
      this.onChange.emit(this.chart);
    }
  }

  private setBreakdownOptions() {
    this.breakdownOptions = [ChartBreakdownStaticOptions.createdBy];
    if (this.template?.isOrganisationTemplate) {
      this.breakdownOptions.push(ChartBreakdownStaticOptions.project, ChartBreakdownStaticOptions.team);
    }
    if (this.templatesService.isWorkflow(this.template)) {
      this.breakdownOptions = [ChartBreakdownStaticOptions.workflowColumns].concat(...this.breakdownOptions);
    }

    this.template.items.forEach((field) => {
      // add list items for breakdown
      if (field.kind === FieldKinds.Category) {
        this.breakdownOptions.push({
          label: field.description,
          value: field._id,
        });
      }
    });
  }

  private getDateUnits(dateRange: momentRange.DateRange) {
    const week = range(moment(), moment().add(1, 'week'));
    const month = range(moment(), moment().add(1, 'month'));
    const dateUnits = [ChartDataIntervalOptions[ChartDataIntervals.Day]];
    if (dateRange >= week) {
      dateUnits.push(ChartDataIntervalOptions[ChartDataIntervals.Week]);
    }
    if (dateRange >= month) {
      dateUnits.push(ChartDataIntervalOptions[ChartDataIntervals.Month]);
    }
    return dateUnits;
  }
}
